<template>
  <div class="text-sm md:text-base">
    <div class="mb-6 md:mb-18">
      <div class="row gutter-y-0">
        <div class="w-full md:w-1/2">
          <form @submit.prevent>
            <full-text-search-component
              class="border-b relative mt-8 flex border-black"
            />
            <div class="mt-5 flex items-center justify-between">
              <label class="checkbox">
                {{ t('open_content') }}
                <span class="relative">
                  <input
                    type="checkbox"
                    name="open-content"
                    v-model="basicFilter.openContent"
                  />
                  <svg
                    width="27"
                    height="27"
                    viewBox="0 0 24 24"
                    focusable="false"
                    aria-hidden="true"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="11"
                      stroke-width="1"
                      stroke="black"
                      class="checkbox-mark"
                      vector-effects="non-scaling-stroke"
                      fill="none"
                    />
                  </svg>
                </span>
              </label>

              <button
                class="button-filter md:hidden"
                @click="toggleFilter"
                :aria-pressed="filterVisible ? 'true' : 'false'"
              >
                {{ t('filter_toggle_label') }}
                <span class="md:hidden" v-if="numberOfSetFilters"
                  >({{ numberOfSetFilters }})</span
                >
              </button>
            </div>
          </form>
        </div>
        <div class="order-first ml-auto w-full md:order-last md:w-2/5">
          <slot name="nav" />
        </div>
      </div>
    </div>

    <div class="mb-12 hidden md:block" ref="summary">
      <filter-summary-component :total-count="totalCount" />
    </div>
    <div class="mb-8">
      <search-controls-component />
    </div>

    <div class="row" :class="layoutClass">
      <div
        class="filter-wrapper relative hidden md:order-last md:block"
        v-if="filterVisible"
      >
        <detail-filter-component
          :loading="loadingFilters"
          class="sticky top-103px max-h-screen overflow-y-auto"
        ></detail-filter-component>
      </div>
      <div class="flex-1" :class="{ 'opacity-20': loading }">
        <ul v-if="objects.length" class="object-list">
          <li
            v-for="(object, index) in objects"
            :key="object.id"
            class="object-teaser-wrapper"
          >
            <object-teaser-component
              :object="object"
              :persist-search="true"
              :index="index + pagination.skip"
            ></object-teaser-component>
          </li>
        </ul>

        <p v-else-if="loading" class="py-4 text-center text-xl font-normal">
          {{ t('loading') }}
        </p>

        <div v-else-if="firstRequest" class="space-y-4">
          <p>
            {{ t('no_results_message') }}
          </p>

          <slot name="noresult" />
        </div>

        <div class="flex">
          <pagination-component
            v-if="totalCount > pagination.pageSize"
            class="mx-auto mt-20"
            :pagination="pagination"
            :total-count="totalCount"
            @prev="prev"
            @next="next"
            @goto="goto"
          />
        </div>
      </div>
    </div>

    <transition
      enter-active-class="transition-opacity duration-100 ease-out"
      leave-active-class="transition-opacity duration-100 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        class="fixed inset-0 z-20 flex flex-col overflow-y-auto bg-white px-5 pb-5 md:hidden"
        v-if="filterVisible"
        v-cloak
      >
        <div class="flex flex-col space-y-5 pb-18">
          <div class="sticky top-0 z-20 space-y-10 bg-white py-5">
            <header class="flex">
              <span class="text-xl">{{ t('filter_toggle_label') }}</span>
              <button @click="toggleFilter" class="ml-auto">
                <span class="sr-only">Close</span>
                <svg
                  width="20"
                  height="20"
                  focusable="false"
                  aria-hidden="true"
                >
                  <use xlink:href="#close"></use>
                </svg>
              </button>
            </header>
            <filter-summary-component :total-count="totalCount" />
          </div>

          <div class="over flex-auto">
            <detail-filter-component
              :loading="loadingFilters"
            ></detail-filter-component>
          </div>

          <button
            v-if="numberOfSetFilters"
            class="fixed inset-x-0 bottom-0 z-10 w-full bg-black p-5 text-lg text-white"
            @click="toggleFilter"
          >
            {{ t('view_results') }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<i18n>
{
  "en": {
    "open_content": "Open Content",
    "view_results": "View results",
    "loading": "Loading…",
    "filter_toggle_label": "Detailed search",
    "no_results_message": "No results. Please change your search filter to get a result."
  },
  "de": {
    "open_content": "Open Content",
    "view_results": "Zeige Ergebnisse",
    "loading": "Lädt…",
    "filter_toggle_label": "Detailsuche",
    "no_results_message": "Keine Treffer. Bitte verändern Sie die Suchfilter, um zu einem Ergebnis zu kommen."
  }
}
</i18n>

<script lang="ts">
import smoothscroll from 'smoothscroll-polyfill';
import {
  Ref,
  computed,
  defineComponent,
  getCurrentInstance,
  onMounted,
  ref,
  watch,
  provide,
} from 'vue';
import { AvailableFilters } from '../@types/filter';
import {
  availableFilters,
  basicFilter,
  computedFilter,
  datingFilter,
  colorFilter,
  filterVisible,
  layout,
  pagination,
  sanitizedFilter,
  sort,
  toggleFilter,
} from '../utils/filter';
import { createApi } from '../utils/gql';
import getObjects from '../utils/objects';
import { loading } from '../utils/search';
import { dispatchWindowEvent, lang, mqLarge } from '../utils/utils';
import FilterSummaryComponent from './FilterSummaryComponent.vue';
import FullTextSearchComponent from './FullTextSearchComponent.vue';
import ObjectTeaserComponent from './ObjectTeaserComponent.vue';
import PaginationComponent from './PaginationComponent.vue';
import SearchControlsComponent from './SearchControlsComponent.vue';
import DetailFilterComponent from './DetailFilterComponent.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  components: {
    FullTextSearchComponent,
    SearchControlsComponent,
    ObjectTeaserComponent,
    FilterSummaryComponent,
    PaginationComponent,
    DetailFilterComponent,
  },

  props: {
    allColors: {
      type: Array as PropType<string[]>,
      required: false,
    },
  },

  setup(props) {
    const instance = getCurrentInstance();

    const objects = ref([]);
    const totalCount = ref(0);
    const loadingFilters = ref(false);
    const firstRequest = ref(false);
    const api = createApi();

    provide('allColors', props.allColors);

    const fetchObjects = () =>
      new Promise<void>((resolve, reject) => {
        loading.value = true;
        loadingFilters.value = true;
        getObjects(
          api,
          lang,
          sanitizedFilter.value,
          pagination,
          sort.value
        ).then((result) => {
          if (result) {
            objects.value = result.objects;
            totalCount.value = result.totalCount;
            loading.value = false;
            loadingFilters.value = false;
            firstRequest.value = true;

            Object.keys(result.filter).forEach((key) => {
              availableFilters[key as keyof AvailableFilters] =
                result.filter[key as any];
            });

            instance?.proxy.$nextTick(() => {
              dispatchWindowEvent('matchheights');
            });
            resolve();
          } else {
            reject();
          }
        });
      });

    onMounted(() => {
      smoothscroll.polyfill();

      watch(
        () => pagination.skip,
        (value) => {
          if (value || value === 0) {
            fetchObjects();
          }
        },
        { immediate: false }
      );
      watch(
        () => pagination.pageSize,
        (value) => {
          if (value) {
            pagination.skip = 0;
            fetchObjects();
          }
        },
        { immediate: false }
      );
      watch(
        sort,
        (value) => {
          if (value) {
            fetchObjects();
          }
        },
        { immediate: false }
      );
      watch(
        sanitizedFilter,
        (value) => {
          if (value) {
            pagination.skip = 0;
            fetchObjects();
          }
        },
        { immediate: false }
      );

      fetchObjects().then(() => {
        if (!computedFilter.value.length) {
          if (mqLarge.matches) {
            filterVisible.value = true;
          }
        }
      });
    });

    const summary: Ref<HTMLElement | null> = ref(null);
    const scrollToTop = () => {
      if (summary.value) {
        window.scrollTo({
          top: summary.value.offsetTop - 16,
          behavior: 'smooth',
        });
      }
    };

    const prev = (): void => {
      pagination.skip -= pagination.pageSize;
      scrollToTop();
    };

    const next = (): void => {
      pagination.skip += pagination.pageSize;
      scrollToTop();
    };

    const goto = (value: number): void => {
      pagination.skip = (value - 1) * pagination.pageSize;
      scrollToTop();
    };

    const layoutClass = computed(() => {
      return `layout-${layout.value.toLowerCase()} ${
        filterVisible.value ? 'filter-visible' : ''
      }`;
    });

    watch(layout, () => {
      dispatchWindowEvent('resize');
    });

    const numberOfSetFilters = computed((): number => {
      const numberOfSetVocabularyFilters = computedFilter.value.length;
      const dateFilterSet = datingFilter.value ? 1 : 0;
      const openContentSet = basicFilter.openContent ? 1 : 0;

      return (
        numberOfSetVocabularyFilters +
        dateFilterSet +
        openContentSet +
        colorFilter.value.length
      );
    });

    return {
      filterVisible,
      objects,
      pagination,
      totalCount,
      prev,
      next,
      goto,
      layoutClass,
      toggleFilter,
      summary,
      basicFilter,
      numberOfSetFilters,
      loading,
      loadingFilters,
      firstRequest,
      ...useI18n(),
    };
  },
});
</script>
